"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"
import { useThemeConfig } from "@/hooks/use-theme-config"

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  )
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

/** The pop-up surface every hover in the app draws: TooltipContent below and the charts' ChartTooltipCard. */
function useTooltipSurface() {
  const { isTerminal, hasGlow } = useThemeConfig()

  // Hydration-safe pattern: only show terminal styling after mount
  const mounted = React.useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  )

  // Use safe values during SSR
  const showTerminal = mounted && isTerminal
  const showGlow = mounted && hasGlow

  return {
    showTerminal,
    className: cn(
      "z-50 w-fit rounded-md px-3 py-1.5 text-xs text-balance",
      showTerminal
        ? [
            // Terminal mode: Bloomberg-style with emerald colors, static glow shadow (no animation)
            "bg-black border border-emerald-500 text-emerald-400 font-mono",
            showGlow && "shadow-[0_0_15px_rgba(16,185,129,0.4),0_0_30px_rgba(16,185,129,0.2)]",
          ]
        : "bg-popover text-popover-foreground border border-border shadow-md"
    ),
  }
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  const { showTerminal, className: surface } = useTooltipSurface()

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          surface,
          // No animations in terminal mode
          !showTerminal &&
            "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          className={cn(
            showTerminal
              ? "fill-black z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] border-r border-b border-emerald-500"
              : "fill-popover z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]"
          )}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, useTooltipSurface }
